import React, { useEffect } from "react"
import PropTypes from "prop-types"
import { useParams } from "react-router-dom"
import { Row, Col, Skeleton, Flex } from "antd"
import Avatar from "components/common/Avatar"
import { useDispatch, useSelector } from "react-redux"
import { isGuideSelector } from "store/user"
import GuideContact from "../GuideContact/index"
import styles from "./index.module.css"
import { getGuideProfile, cleanedGuideProfiles } from "store/guide-profile"
import { useTranslation } from "react-i18next"
import { Title } from "ui/Typography"
import Layer from "assets/icons/layers.svg"
import { StarOutlined } from "@ant-design/icons"
import { StarIcon } from "ui/Icon/DSIcon/starIcon"

const GuideHeader = () => {
  const loggedIn = useSelector((store) => store.auth.loggedIn)
  const isGuide = useSelector(isGuideSelector)
  const userLoading = useSelector((store) => store.user?.loading)
  const dispatch = useDispatch()

  const loading = useSelector((store) => store.guideProfile.guide.loading)
  const guide = useSelector((store) => store.guideProfile?.guide?.data)
  const { slug } = useParams()
  const { t } = useTranslation()

  useEffect(() => {
    dispatch(getGuideProfile(slug))
    return () => dispatch(cleanedGuideProfiles())
  }, [slug, dispatch])
  const classes = [styles.guideHeader]

  const renderGuideContactMessage = (loggedIn, isGuide, guide, userLoading) => {
    // Don't show contact message for guides, when not logged in, or when user data is still loading
    if (!loggedIn || isGuide || userLoading) {
      return null
    }

    const guideWithSplitName = {
      ...guide,
      user: {
        ...guide?.user,
        first_name: "",
        last_name: ""
      },
      user_id: guide?.id
    }

    if (guide?.display_name) {
      const nameParts = guide.display_name.trim().split(/\s+/)
      guideWithSplitName.user.first_name = nameParts[0] || ""
      guideWithSplitName.user.last_name = nameParts.slice(1).join(" ") || ""
    }
    return <GuideContact client={guideWithSplitName} />
  }

  return (
    <div className={classes.join(" ")}>
      <Row gutter={[10, 10]} align="middle">
        <Col xl={2} lg={2} md={3} sm={3} xs={3} xxs={24}>
          <div className={`${styles.avatarContainer} ${guide?.top_rated ? styles.topRated : ""}`}>
            <Avatar user={guide} size={78} />
            {guide?.top_rated && (
              <div className={styles.topRatedTag}>
                <StarOutlined style={{ marginRight: 4 }} /> Top rated
              </div>
            )}
          </div>
        </Col>
        <Col xl={18} lg={18} md={17} sm={15} xs={21} xxs={24}>
          <Skeleton loading={loading} active paragraph={{ rows: 1 }}>
            <Title level={4} className={styles.name}>
              {guide?.display_name}
            </Title>
            <Flex className={styles.locationContainer}>{guide?.display_location}</Flex>
            <Flex gap={8} className={styles.ratingContainer}>
              <div className={styles.star}>
                <StarIcon />
              </div>
              <span className={styles.ratingValue}>{guide?.rating || "0.0"}</span>
              <span className="reviews">{"(" + guide?.reviewer_count + ")" || 0}</span>
              <span className="experience">
                <img src={Layer} alt="Layer" style={{ marginRight: 4 }} />
                {guide?.published_experience_count || 0} {t("guide.navigation.experiences.label")}
              </span>
            </Flex>
          </Skeleton>
        </Col>
        <Col xl={4} lg={4} md={4} sm={6} xs={24}>
          {renderGuideContactMessage(loggedIn, isGuide, guide, userLoading)}
        </Col>
      </Row>
    </div>
  )
}

GuideHeader.propTypes = {
  guide: PropTypes.shape({
    user_id: PropTypes.string.isRequired,
    display_name: PropTypes.string.isRequired,
    city: PropTypes.string,
    country: PropTypes.string,
    rating: PropTypes.number.isRequired
  }).isRequired
}

export default GuideHeader
